import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { X, ArrowUpRight, Share } from "lucide-react";

/*
 * InstallPrompt
 * -------------
 * A minimalist premium invitation to add the app to the reader's home
 * screen. Appears once the reader has scrolled past ~1.2 viewport
 * heights on any page (so first-time visitors see it wherever they
 * land). Dismissable, with a 14-day cool-down persisted in
 * localStorage.
 *
 * Two variants:
 *   • Chrome / Android: uses the deferred beforeinstallprompt() flow.
 *   • iOS Safari:       shows a two-line hint ("Tap Share, then Add to
 *                       Home Screen.") since iOS provides no native
 *                       install API.
 *
 * Copy is warm, human, and exclusive. No em-dashes.
 */
const COOLDOWN_KEY = "gvg-install-cooldown";
const COOLDOWN_DAYS = 14;
const SCROLL_TRIGGER_MULT = 1.2;
const HIDE_PATHS = ["/admin", "/diagnosis-pro"];

function isCoolingDown() {
  try {
    const raw = localStorage.getItem(COOLDOWN_KEY);
    if (!raw) return false;
    const until = parseInt(raw, 10);
    return Number.isFinite(until) && Date.now() < until;
  } catch (e) {
    return false;
  }
}

function setCooldown() {
  try {
    const until = Date.now() + COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(COOLDOWN_KEY, String(until));
  } catch (e) {
    /* noop */
  }
}

export default function InstallPrompt() {
  const { pathname } = useLocation();
  const reduced = useReducedMotion();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [scrollReached, setScrollReached] = useState(false);

  // Capture the beforeinstallprompt event as early as possible + iOS detect
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (isCoolingDown()) return undefined;
    const ua = window.navigator.userAgent || "";
    const iOS = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua);
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return undefined;
    setIsIOS(iOS);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  // Scroll gate - listen on every page except admin/diagnosis-pro
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (HIDE_PATHS.some((p) => pathname.startsWith(p))) {
      setScrollReached(false);
      return undefined;
    }
    if (isCoolingDown()) return undefined;
    const trigger = () => {
      const threshold = window.innerHeight * SCROLL_TRIGGER_MULT;
      if (window.scrollY >= threshold) {
        setScrollReached(true);
        window.removeEventListener("scroll", trigger);
      }
    };
    window.addEventListener("scroll", trigger, { passive: true });
    trigger();
    return () => window.removeEventListener("scroll", trigger);
  }, [pathname]);

  // Show when scroll gate passes AND (deferred prompt exists OR iOS Safari)
  useEffect(() => {
    if (!scrollReached) return;
    if (deferred || isIOS) setVisible(true);
  }, [scrollReached, deferred, isIOS]);

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice.catch(() => null);
    } finally {
      setCooldown();
      setVisible(false);
    }
  };

  const dismiss = () => {
    setCooldown();
    setVisible(false);
  };

  if (HIDE_PATHS.some((p) => pathname.startsWith(p))) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="install-prompt"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="fixed z-40 left-1/2 -translate-x-1/2 bottom-6 md:bottom-10 w-[min(560px,calc(100vw-32px))]"
          data-testid="install-prompt"
        >
          <div
            className="relative rounded-2xl border border-brass/40 bg-black/70 backdrop-blur-xl px-5 py-4 md:px-6 md:py-5 overflow-hidden"
            style={{
              boxShadow:
                "0 24px 60px -20px rgba(0,0,0,0.65), 0 0 40px rgba(201,169,97,0.14), inset 0 1px 0 rgba(245,241,232,0.05)",
            }}
          >
            {/* Top-edge champagne highlight */}
            <span
              aria-hidden
              className="absolute inset-x-6 top-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(201,169,97,0.65), transparent)",
              }}
            />
            {/* soft champagne bloom behind */}
            <span
              aria-hidden
              className="absolute inset-0 pointer-events-none opacity-70"
              style={{
                background:
                  "radial-gradient(ellipse at 20% 50%, rgba(201,169,97,0.10), transparent 55%)",
              }}
            />

            <div className="relative flex items-center gap-4">
              {/* pulsing brass dot */}
              <motion.span
                aria-hidden
                className="h-1.5 w-1.5 rounded-full bg-brass shrink-0"
                animate={reduced ? {} : { opacity: [1, 0.35, 1] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: [0.4, 0, 0.6, 1] }}
              />

              <div className="flex-1 min-w-0">
                <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
                  Keep the engine on your home screen
                </p>
                <p className="mt-1.5 font-serif italic text-ink text-[15px] leading-snug">
                  Add Get Virtual Growth to your home screen.
                </p>

                {isIOS ? (
                  <p className="mt-2.5 font-sans text-[11px] text-ink-muted leading-relaxed inline-flex items-center gap-1.5">
                    <Share className="h-3 w-3 text-brass" strokeWidth={1.6} />
                    Tap Share, then Add to Home Screen.
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={install}
                    data-testid="install-prompt-cta"
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-brass hover:bg-brass/85 px-4 py-1.5 font-sans text-[10px] uppercase tracking-mega font-semibold text-bg transition-colors duration-500"
                  >
                    Add to home screen
                    <ArrowUpRight className="h-3 w-3" />
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={dismiss}
                data-testid="install-prompt-dismiss"
                aria-label="Not now"
                className="shrink-0 text-ink-muted hover:text-brass transition-colors duration-500"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
